import React from 'react';
import { Snapshot, AIIntent, AgentRole } from '../types';
import { History, GitBranch, Database, ShieldAlert, HardHat } from 'lucide-react';

interface SnapshotExplorerProps {
  snapshots: Snapshot[];
  onRestore: (snapshot: Snapshot) => void;
}

const IntentBadge = ({ intent }: { intent: AIIntent }) => {
  const colors = {
    [AIIntent.INITIAL_SETUP]: 'bg-purple-500/20 text-purple-300 border-purple-500/50',
    [AIIntent.STRUCTURAL_OPTIMIZATION]: 'bg-blue-500/20 text-blue-300 border-blue-500/50',
    [AIIntent.RISK_MITIGATION]: 'bg-orange-500/20 text-orange-300 border-orange-500/50',
    [AIIntent.FEATURE_EXPANSION]: 'bg-green-500/20 text-green-300 border-green-500/50',
    [AIIntent.UNDEFINED]: 'bg-slate-500/20 text-slate-300 border-slate-500/50',
  };

  return (
    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${colors[intent] || colors[AIIntent.UNDEFINED]}`}>
      {intent.replace('_', ' ')}
    </span>
  );
};

const SnapshotExplorer: React.FC<SnapshotExplorerProps> = ({ snapshots, onRestore }) => {
  if (snapshots.length === 0) {
    return <div className="text-slate-500 text-sm text-center p-4">No snapshots recorded yet.</div>;
  }

  // Reverse to show newest first
  const history = [...snapshots].reverse();

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-widest font-semibold mb-2">
        <Database className="w-3 h-3" />
        <span>Semantic Memory / Versions</span>
      </div>
      
      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
        {history.map((snap) => (
          <div key={snap.id} className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 hover:border-slate-500 transition-colors group">
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2">
                <GitBranch className="w-4 h-4 text-emerald-400" />
                <span className="font-medium text-slate-200 text-sm">{snap.tagName}</span>
              </div>
              <span className="text-xs text-slate-500">
                {new Date(snap.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            
            <div className="flex items-center gap-2 mb-2">
              <IntentBadge intent={snap.intent} />
              {snap.agentRole && (
                  <div className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border ${
                      snap.agentRole === AgentRole.RISK_ANALYST 
                      ? 'bg-orange-900/30 text-orange-400 border-orange-800'
                      : 'bg-indigo-900/30 text-indigo-400 border-indigo-800'
                  }`}>
                      {snap.agentRole === AgentRole.RISK_ANALYST ? <ShieldAlert className="w-3 h-3"/> : <HardHat className="w-3 h-3"/>}
                      {snap.agentRole}
                  </div>
              )}
            </div>

            <p className="text-xs text-slate-400 mb-3 line-clamp-3 italic">
              "{snap.aiReasoning}"
            </p>

            <button 
              onClick={() => onRestore(snap)}
              className="w-full py-1.5 flex items-center justify-center gap-2 text-xs bg-slate-700 hover:bg-slate-600 text-slate-200 rounded transition-colors opacity-0 group-hover:opacity-100"
            >
              <History className="w-3 h-3" />
              Restore this State
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SnapshotExplorer;